// src/components/common/Charts.js
//
// Phase 2 (2026-05-14): thin Shinobi-styled wrappers around recharts
// primitives. Used by the per-PT Dashboards tab and the global sidebar
// Dashboard. Colors match the existing severity badges + brand greens; a
// dark-card aesthetic is baked in so all four charts look consistent.

import {
  Cell,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Pie,
  PieChart,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const SEVERITY_COLORS = {
  critical: '#f87171',  // red-400
  high:     '#fb923c',  // orange-400
  medium:   '#fbbf24',  // amber-400
  low:      '#60a5fa',  // blue-400
  info:     '#94a3b8',  // slate-400
};

const GRADE_COLORS = {
  A: '#34d399',  // emerald-400 (matches shinobi-green family)
  B: '#10b981',  // emerald-500
  C: '#fbbf24',  // amber-400
  D: '#fb923c',  // orange-400
  F: '#f87171',  // red-400
};

const SHINOBI_GREEN = '#48b94c';   // wasabi-500 (primary series on white)

// Shared Shinobi-styled tooltip - a white card with a slate border,
// a color dot per series and a bold value. Replaces recharts' plain dark box.
// `namePrefix` lets a chart prepend e.g. "Grade " to the series name.
function ChartTooltip({ active, payload, label, namePrefix = '' }) {
  if (!active || !payload || !payload.length) return null;
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '8px 11px',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
      }}
    >
      {label !== undefined && label !== null && label !== '' && (
        <div
          style={{
            color: '#64748b',
            fontSize: '10px',
            marginBottom: '5px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </div>
      )}
      {payload.map((p, i) => (
        <div
          key={i}
          style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', lineHeight: '1.6' }}
        >
          <span
            style={{
              width: '9px',
              height: '9px',
              borderRadius: '9999px',
              flexShrink: 0,
              background: p.color || (p.payload && p.payload.fill) || '#48b94c',
            }}
          />
          <span style={{ color: '#475569' }}>{cap(`${namePrefix}${p.name}`)}</span>
          <span style={{ marginLeft: 'auto', paddingLeft: '18px', color: '#0f172a', fontWeight: 700 }}>
            {p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function SpiderChart({ data, height = 260 }) {
  // data: [{category: 'Access Control', value: 23}, ...]
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RadarChart data={data}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="category" tick={{ fill: '#475569', fontSize: 11 }} />
        <PolarRadiusAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} />
        <Radar
          name="Findings"
          dataKey="value"
          stroke={SHINOBI_GREEN}
          fill={SHINOBI_GREEN}
          fillOpacity={0.35}
        />
        <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(34,197,94,0.3)' }} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

export function SeverityPie({ data, height = 220 }) {
  // data: [{name: 'critical', value: 122}, ...]
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={45}
          outerRadius={85}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          stroke="#ffffff"
          strokeWidth={2}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={SEVERITY_COLORS[entry.name] || '#64748b'} />
          ))}
        </Pie>
        <Tooltip content={<ChartTooltip />} />
        <Legend
          verticalAlign="bottom"
          height={32}
          iconSize={8}
          formatter={(v) => <span className="text-xs text-slate-700 capitalize">{v}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function GradeDonut({ data, height = 220 }) {
  // data: [{name: 'A', value: 2}, ...]
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={85}
          paddingAngle={2}
          dataKey="value"
          nameKey="name"
          stroke="#ffffff"
          strokeWidth={2}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={GRADE_COLORS[entry.name] || '#64748b'} />
          ))}
        </Pie>
        <Tooltip content={<ChartTooltip namePrefix="Grade " />} />
        <Legend
          verticalAlign="bottom"
          height={32}
          iconSize={8}
          formatter={(v) => <span className="text-xs text-slate-700">Grade {v}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function RiskOverTimeLine({ data, height = 240, dataKey = 'open_criticals', label = 'Open Criticals' }) {
  // data: [{date: '2026-05-14', open_criticals: 234, total_findings: 1200}, ...]
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" />
        <XAxis
          dataKey="date"
          tick={{ fill: '#64748b', fontSize: 10 }}
          tickFormatter={(d) => d?.slice(5) || d}
        />
        <YAxis tick={{ fill: '#64748b', fontSize: 10 }} allowDecimals={false} width={32} />
        <Tooltip content={<ChartTooltip />} />
        <Line
          type="monotone"
          dataKey={dataKey}
          name={label}
          stroke="#f87171"
          strokeWidth={2}
          dot={{ r: 3, fill: '#f87171', stroke: 'rgba(248,113,113,0.4)', strokeWidth: 4 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export const CHART_COLORS = { SEVERITY_COLORS, GRADE_COLORS, SHINOBI_GREEN };
